import { useState } from 'react';
import { usersApi } from '@/lib/api/users';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loading } from '@/components/Loading';
import { ConfirmButton } from '@/components/ConfirmButton';
import { EmptyState } from '@/components/ui/empty-state';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Ban, LockOpen, Users, UserMinus } from 'lucide-react';
import type { UserResponse } from '@paladin-monorepo/common';
import { CreateUserDialog } from '../users/CreateUserDialog';
import { EditUserDialog } from '../users/EditUserDialog';
import { useAuth } from '@/lib/auth';

export function UsersSettingsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserResponse | null>(null);

  const { user: currentUser } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'UsersSettingsTab',
  });

  const { data: users, isLoading } = useQuery<UserResponse[]>({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const blockMutation = useMutation({
    mutationFn: (userId: string) => usersApi.blockAccountUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const unblockMutation = useMutation({
    mutationFn: (userId: string) => usersApi.unblockAccountUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => usersApi.removeAccountUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>{t('users.title')}</CardTitle>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('users.list.addUser')}
          </Button>
        </CardHeader>
        <CardContent>
          {!users || users.length === 0 ? (
            <EmptyState
              icon={Users}
              title={t('users.list.empty.title')}
              description={t('users.list.empty.description')}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('users.list.columns.email')}</TableHead>
                  <TableHead>{t('users.list.columns.name')}</TableHead>
                  <TableHead>{t('users.list.columns.role')}</TableHead>
                  <TableHead>{t('users.list.columns.status')}</TableHead>
                  <TableHead>{t('users.list.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.email}
                      {!user.verifiedAt && (
                        <Badge variant="outline" className="ml-2">
                          {t('users.list.notVerified')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {t(`codebook.role.${user.role}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.blockedAt ? (
                        <Badge variant="destructive">
                          {t('users.list.status.blocked')}
                        </Badge>
                      ) : (
                        <Badge variant="default">
                          {t('users.list.status.active')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingUser(user)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {user.id.toString() !== currentUser?.id?.toString() && user.blockedAt && (
                          <ConfirmButton
                            title={t('users.actions.confirmUnblock.title')}
                            description={t('users.actions.confirmUnblock.description')}
                            onConfirm={() => unblockMutation.mutateAsync(user.id)}
                            variant="ghost"
                            disabled={unblockMutation.isPending}
                          >
                            <LockOpen className="h-4 w-4" />
                          </ConfirmButton>
                        )}
                        {user.id.toString() !== currentUser?.id?.toString() && !user.blockedAt && (
                          <ConfirmButton
                            title={t('users.actions.confirmBlock.title')}
                            description={t('users.actions.confirmBlock.description')}
                            onConfirm={() => blockMutation.mutateAsync(user.id)}
                            variant="ghost"
                            disabled={blockMutation.isPending}
                          >
                            <Ban className="h-4 w-4" />
                          </ConfirmButton>
                        )}
                        {user.id.toString() !== currentUser?.id?.toString() && (
                          <ConfirmButton
                            title={t('users.actions.confirmRemove.title')}
                            description={t('users.actions.confirmRemove.description')}
                            onConfirm={() => removeMutation.mutateAsync(user.id)}
                            variant="ghost"
                            disabled={removeMutation.isPending}
                          >
                            <UserMinus className="h-4 w-4" />
                          </ConfirmButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateUserDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {editingUser && (
        <EditUserDialog
          user={editingUser}
          open={!!editingUser}
          onOpenChange={(open) => !open && setEditingUser(null)}
        />
      )}
    </>
  );
}
